/**
 * serviceQueue — durable offline mutation queue for service-entry adds.
 *
 * Backed by idb-keyval (single IndexedDB key holding an array). Survives tab
 * close / app reload. Cleared on sign-out to prevent cross-user leaks (§00).
 *
 * Max 100 items; older pending items are silently dropped when the cap is hit
 * (hobby-mechanic volume; cap exists to keep storage bounded).
 */

import type { NewServiceEntry } from '@/hooks/useAddServiceEntry';
import { get, set } from 'idb-keyval';

const KEY = 'gg.serviceQueue.v1';
const MAX_QUEUE = 100;

// Change notification. Every mutation notifies subscribers so the UI re-reads
// IMMEDIATELY — critical for the optimistic insert: an offline `add()` enqueues
// with no network event to piggyback on, so without this the new entry wouldn't
// appear until the next 30s drain tick. useServiceQueue subscribes.
const listeners = new Set<() => void>();

/** Subscribe to queue mutations; returns an unsubscribe fn. */
export function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function notify(): void {
  for (const fn of listeners) {
    try {
      fn();
    } catch {
      // a subscriber throwing must not break a queue mutation
    }
  }
}

/**
 * Spec 011 — a before/after photo slot not yet known to be committed.
 * Exactly one of the two fields is set at any time:
 *   - `clientId` — the photo is still in photoQueue (uploading/committing).
 *     Resolved to `photoId` by the photo-drain in usePhotoQueue.ts once its
 *     upload+commit succeeds (see the write-back there).
 *   - `photoId` — upload+commit succeeded, but the photo isn't visible as
 *     `status === 'committed'` in GET /v1/photos yet (the async strip-worker
 *     hasn't finished). Resolved by the service-queue drain below, which
 *     polls for that status transition.
 * Once a role's photo shows `committed`, the drain merges the plain photoId
 * into `entry.beforePhotoId`/`afterPhotoId` (lamport's revised DTO — the
 * server resolves the id to its authoritative {photoId, sha256} internally;
 * the client never builds or sends a sha256) and removes the role from
 * `pendingPhotoRefs` entirely.
 */
export interface PendingPhotoRef {
  clientId?: string;
  photoId?: string;
}

/**
 * Spec 011 — the exact error code the server returns (409, AppError envelope
 * `{ error: { code, message } }`) for an un-committed/un-stripped photo id.
 * Match on this, never bare HTTP 409 — a 409 can also mean an idempotency
 * conflict, which must NOT be treated as a re-upload prompt.
 */
export const PHOTO_NOT_READY_CODE = 'PHOTO_NOT_READY';

/**
 * Spec 011 — shared copy for a photo-not-ready rejection (see
 * useAddServiceEntry.ts's and useServiceQueue.ts's file headers). Exported
 * so both the synchronous fast-path error and the deferred-drain's ledger
 * row show identical wording.
 */
export const PHOTO_NOT_READY_MESSAGE =
  'One of the attached photos needs re-uploading. Remove it and add a new one, then save again.';

export interface QueuedEntry {
  /** crypto.randomUUID() — dedup key + server idempotency key. */
  clientMutationId: string;
  /** Owner uid stamped at enqueue time — drain refuses items where uid ≠ currentUser. */
  uid: string;
  vehicleId: string;
  entry: NewServiceEntry;
  /** ISO timestamp for optimistic ordering. */
  queuedAt: string;
  status: 'pending' | 'inflight' | 'failed';
  /**
   * Spec 011 — before/after photo slots not yet known to be committed at
   * enqueue time. The drain in useServiceQueue.ts holds this entry (stays
   * "pending", never attempts the POST) until every role here is either
   * resolved into `entry.beforePhotoId`/`afterPhotoId` or abandoned (photo
   * discarded/never recoverable). Absent once every attached photo is
   * already committed at enqueue time (the common case: entry saved after
   * photos already finished uploading).
   */
  pendingPhotoRefs?: {
    before?: PendingPhotoRef;
    after?: PendingPhotoRef;
  };
  /**
   * Spec 011 — set alongside `status: 'failed'` when the failure was a 409
   * photo-not-ready rejection, so the ledger row can show a re-upload
   * prompt (lamport) instead of the generic "not saved" copy.
   */
  failureReason?: 'photo_not_ready';
}

async function readAll(): Promise<QueuedEntry[]> {
  return (await get<QueuedEntry[]>(KEY)) ?? [];
}

async function writeAll(items: QueuedEntry[]): Promise<void> {
  await set(KEY, items);
}

/** Append a new item. Drops oldest pending items if cap is exceeded. */
export async function enqueue(item: QueuedEntry): Promise<void> {
  const current = await readAll();
  const next = [...current, item];
  // Cap: keep the newest MAX_QUEUE items.
  const capped = next.length > MAX_QUEUE ? next.slice(next.length - MAX_QUEUE) : next;
  await writeAll(capped);
  notify();
}

/** All items across all vehicles. */
export async function listAll(): Promise<QueuedEntry[]> {
  return readAll();
}

/** Items for a specific vehicle. */
export async function listFor(vehicleId: string): Promise<QueuedEntry[]> {
  const all = await readAll();
  return all.filter((q) => q.vehicleId === vehicleId);
}

/**
 * Patch a queued item by clientMutationId (status update, or — spec 011 —
 * resolving/clearing pendingPhotoRefs and merging photo ids into `entry`, or
 * tagging a `failureReason`). Pass `pendingPhotoRefs: undefined` to clear it
 * once every role resolves.
 */
export async function update(
  clientMutationId: string,
  patch: Partial<Pick<QueuedEntry, 'status' | 'entry' | 'failureReason'>> & {
    pendingPhotoRefs?: QueuedEntry['pendingPhotoRefs'] | undefined;
  },
): Promise<void> {
  const all = await readAll();
  await writeAll(
    all.map((q) => {
      if (q.clientMutationId !== clientMutationId) return q;
      const next = { ...q, ...patch };
      if ('pendingPhotoRefs' in patch && patch.pendingPhotoRefs === undefined) {
        delete next.pendingPhotoRefs;
      }
      return next as QueuedEntry;
    }),
  );
  notify();
}

/** Remove a queued item (on successful POST or discard). */
export async function remove(clientMutationId: string): Promise<void> {
  const all = await readAll();
  await writeAll(all.filter((q) => q.clientMutationId !== clientMutationId));
  notify();
}

/**
 * Clear the entire queue. Called on sign-out to prevent User A's unsynced
 * entries from replaying under User B on a shared device (§00 SOC2 BLOCKER).
 */
export async function clearAll(): Promise<void> {
  await set(KEY, []);
  notify();
}
